import React, { useState } from "react";
import { Helmet } from "react-helmet";

const episodes = [
  {
    id: 1,
    number: "001",
    title: "Why Custom-Coded Websites Beat WordPress for Junk Removal",
    description:
      "We break down why a custom-built website gives junk removal businesses a real edge over generic WordPress setups — from speed and SEO to conversion and control.",
    duration: "Listen on Apple",
    date: "2025",
    url: "https://podcasts.apple.com/us/podcast/why-custom-coded-websites-beat-wordpress-for-junk-removal/id1864004330?i=1000769039880",
  },
  {
    id: 2,
    number: "002",
    title: "Stop Chasing Sales: Start Building Customers Who Never Leave",
    description:
      "The mindset shift every junk removal owner needs to make — from grinding for the next job to building a loyal customer base that books again and refers others.",
    duration: "Listen on Apple",
    date: "2025",
    url: "https://podcasts.apple.com/us/podcast/stop-chasing-sales-start-building-customers-who-never/id1864004330?i=1000769212753",
  },
];

const APPLE_URL =
  "https://podcasts.apple.com/us/podcast/reflex-advisor-junk-removal-growth-system/id1864004330";

export default function Podcast() {
  const [playingId, setPlayingId] = useState(null);

  const handleApple = () => {
    window.open(APPLE_URL, "_blank");
  };

  return (
    <React.Fragment>
      <Helmet>
        <title>Podcast | Reflex Advisor – Junk Removal Growth System</title>
        <meta
          name="description"
          content="Listen to the Reflex Advisor podcast — real strategies for scaling junk removal businesses from 5 to 6 figures using systems, automation, and C-suite thinking."
        />
        <style>{`
          html, body {
            margin: 0; padding: 0;
            background: #000;
            color: #fff;
            font-size: 16px;
            overflow-x: hidden;
          }
        `}</style>
      </Helmet>

      {/* ─── HERO ─── */}
<section className="pod-hero">
  <div className="pod-hero-inner">
    <p className="pod-eyebrow">The Reflex Advisor Podcast</p>
    <h1 className="pod-hero-h1">
      Junk Removal<br />
      <span className="pod-gradient">Growth System</span>
    </h1>
    <p className="pod-hero-sub">
      Real talk on scaling service businesses — lead generation, automation, sales systems, and the mindset it takes to go from operator to owner.
    </p>
    <button className="pod-btn-primary" onClick={handleApple}>
      Listen on Apple Podcasts
    </button>
  </div>
</section>

      {/* ─── STATS STRIP ─── */}
      <div className="pod-stats-strip">
        {[
          { label: "Episodes", value: "2" },
          { label: "Avg. Listen Time", value: "29 min" },
          { label: "Industries Served", value: "Service Biz" },
          { label: "Guarantee", value: "Real Results" },
        ].map((s) => (
          <div className="pod-stat" key={s.label}>
            <span className="pod-stat-val">{s.value}</span>
            <span className="pod-stat-label">{s.label}</span>
          </div>
        ))}
      </div>

      {/* ─── FEATURED BADGE ─── */}
      <section className="pod-featured-strip">
  <div className="pod-featured-inner">
    <p className="pod-featured-eyebrow">As Seen On</p>

    <h2 className="pod-featured-h2">
      Officially <span>Listed</span> &amp; Featured
    </h2>

    <p className="pod-featured-sub">
      Recognized and indexed across top podcast discovery platforms.
    </p>

    <a
      href="https://podstatus.com/podcasts/reflex-advisor-junk-removal-growth-system-1381602"
      target="_blank"
      rel="noopener noreferrer"
      className="pod-featured-card"
    >
      <div className="pod-featured-card-icon">📡</div>

      <div className="pod-featured-card-body">
        <span className="pod-featured-card-name">PodStatus.com</span>
        <span className="pod-featured-card-desc">View our listing ↗</span>
      </div>
    </a>
  </div>
      </section>

      {/* ─── EPISODES ─── */}
      <section className="pod-episodes">
        <div className="pod-ep-inner">
          <p className="pod-eyebrow pod-eyebrow--dark">Latest Episodes</p>
          <h2 className="pod-ep-h2">Recent Drops</h2>

          <div className="pod-ep-list">
            {episodes.map((ep) => (
              <div
                className={`pod-ep-card ${playingId === ep.id ? "pod-ep-card--active" : ""}`}
                key={ep.id}
              >
                <div className="pod-ep-num">{ep.number}</div>
                <div className="pod-ep-body">
                  <h3 className="pod-ep-title">{ep.title}</h3>
                  <p className="pod-ep-desc">{ep.description}</p>
                  <div className="pod-ep-meta">
                    <span className="pod-ep-date">{ep.date}</span>
                    <span className="pod-ep-dot">·</span>
                    <a
                      href={ep.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="pod-ep-apple-link"
                    >
                      Listen on Apple Podcasts ↗
                    </a>
                  </div>
                </div>
                <button
                  className="pod-ep-play"
                  onClick={() => window.open(ep.url, "_blank")}
                  aria-label={`Listen to ${ep.title} on Apple Podcasts`}
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M5 3.5L15 9L5 14.5V3.5Z" fill="white" />
                  </svg>
                </button>
              </div>
            ))}
          </div>

          <div className="pod-ep-cta">
            <p className="pod-ep-cta-text">All episodes available on Apple Podcasts</p>
            <button className="pod-btn-primary" onClick={handleApple}>
              Listen on Apple Podcasts →
            </button>
          </div>
        </div>
      </section>

      {/* ─── ABOUT STRIP ─── */}
      <section className="pod-about">
        <div className="pod-about-inner">
          <div className="pod-about-badge">About the Show</div>
          <h2 className="pod-about-h2">
            "We don't talk theory.<br />We talk execution."
          </h2>
          <p className="pod-about-body">
            Every episode is built around one idea — that service businesses
            deserve real systems, real leads, and real revenue growth. Hosted by
            the Reflex Advisor team in Toronto, this show is for junk removal
            owners who are ready to stop guessing and start scaling.
          </p>
          <div className="pod-about-rule" />
          <p className="pod-about-note">
            Family-owned · Toronto · Done-for-you growth
          </p>
        </div>
      </section>

      <style>{`
        :root {
          --purple: #7b19bc;
          --purple-light: #d18cf3;
          --purple-hover: #9b24e8;
          --font-display: -apple-system, 'SF Pro Display', BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
          --font-body: -apple-system, 'SF Pro Text', BlinkMacSystemFont, 'Helvetica Neue', sans-serif;
        }

        /* ── HERO ── */
.pod-hero {
  background: #000;
  padding: 140px 24px 120px;
  overflow: hidden;
}
.pod-hero-inner {
  max-width: 720px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.pod-eyebrow {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--purple-light);
  margin: 0 0 20px;
}
.pod-eyebrow--dark { color: var(--purple); }
.pod-hero-h1 {
  font-family: var(--font-display);
  font-size: 64px;
  font-weight: 700;
  letter-spacing: -0.035em;
  line-height: 1.05;
  color: #fff;
  margin: 0 0 28px;
}
.pod-gradient {
  background: linear-gradient(90deg, #a855f7, #d18cf3, #7b19bc);
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: shimmer 3s linear infinite;
}
@keyframes shimmer {
  0%   { background-position: 0% center; }
  100% { background-position: 200% center; }
}
.pod-hero-sub {
  font-family: var(--font-body);
  font-size: 18px;
  color: rgba(255,255,255,0.6);
  line-height: 1.7;
  margin: 0 0 40px;
  max-width: 520px;
}
.pod-btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 16px 36px;
  background: var(--purple);
  color: #fff;
  font-family: var(--font-display);
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 980px;
  cursor: pointer;
  transition: background 0.25s, transform 0.2s, box-shadow 0.25s;
}
.pod-btn-primary:hover {
  background: var(--purple-hover);
  transform: translateY(-2px);
  box-shadow: 0 8px 28px rgba(123,25,188,0.45);
}

/* ── RESPONSIVE ── */
@media (max-width: 768px) {
  .pod-hero { padding: 100px 20px 80px; }
  .pod-hero-h1 { font-size: 40px; }
  .pod-hero-sub { font-size: 16px; }
  .pod-btn-primary { width: 100%; justify-content: center; }
}
@media (max-width: 480px) {
  .pod-hero-h1 { font-size: 30px; }
}

        /* ── STATS STRIP ── */
        .pod-stats-strip {
          background: #0e0e0e;
          border-top: 1px solid rgba(255,255,255,0.06);
          border-bottom: 1px solid rgba(255,255,255,0.06);
          display: flex;
          align-items: stretch;
          justify-content: center;
          padding: 0;
          overflow: hidden;
        }
        .pod-stat {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 5px;
          padding: 32px 24px;
          border-right: 1px solid rgba(255,255,255,0.06);
        }
        .pod-stat:last-child { border-right: none; }
        .pod-stat-val {
          font-family: var(--font-display);
          font-size: 22px;
          font-weight: 700;
          color: #fff;
          letter-spacing: -0.02em;
        }
        .pod-stat-label {
          font-size: 12px;
          font-weight: 500;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.35);
        }
          
        /* ── FEATURED ── */
.pod-featured-strip {
  background: #000;
  padding: 100px 24px;
  text-align: center;
  border-top: 1px solid rgba(255,186,0,0.2);
  border-bottom: 1px solid rgba(255,186,0,0.2);
}
.pod-featured-inner {
  max-width: 640px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}
.pod-featured-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #ffba00;
  margin: 0;
}
.pod-featured-h2 {
  font-family: var(--font-display);
  font-size: 42px;
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.1;
  color: #fff;
  margin: 0;
}
.pod-featured-h2 span {
  color: #ffba00;
}
.pod-featured-sub {
  font-family: var(--font-body);
  font-size: 16px;
  color: rgba(255,255,255,0.5);
  margin: 0;
  line-height: 1.6;
  max-width: 420px;
}
.pod-featured-card {
  margin-top: 16px;
  display: inline-flex;
  align-items: center;
  gap: 20px;
  padding: 24px 36px;
  background: rgba(255,186,0,0.06);
  border: 1px solid rgba(255,186,0,0.35);
  border-radius: 20px;
  text-decoration: none;
  transition: background 0.25s, transform 0.2s, box-shadow 0.25s;
}
.pod-featured-card:hover {
  background: rgba(255,186,0,0.12);
  transform: translateY(-4px);
  box-shadow: 0 16px 48px rgba(255,186,0,0.15);
}
.pod-featured-card-icon {
  font-size: 36px;
  line-height: 1;
  filter: grayscale(0);
}
.pod-featured-card-body {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}
.pod-featured-card-name {
  font-family: var(--font-display);
  font-size: 26px;
  font-weight: 800;
  color: #ffba00;
  letter-spacing: -0.03em;
  line-height: 1;
}
.pod-featured-card-desc {
  font-size: 13px;
  color: rgba(255,255,255,0.45);
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

        /* ── EPISODES ── */
        .pod-episodes {
          background: #fff;
          padding: 100px 24px 110px;
        }
        .pod-ep-inner {
          max-width: 780px;
          margin: 0 auto;
        }
        .pod-ep-h2 {
          font-family: var(--font-display);
          font-size: 40px;
          font-weight: 700;
          letter-spacing: -0.03em;
          color: #0a0a0a;
          margin: 0 0 52px;
        }
        .pod-ep-list {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .pod-ep-card {
          display: flex;
          align-items: flex-start;
          gap: 24px;
          padding: 28px 0;
          border-bottom: 1px solid #f0eaf8;
          transition: background 0.2s;
        }
        .pod-ep-card:first-child { border-top: 1px solid #f0eaf8; }
        .pod-ep-num {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.15em;
          color: var(--purple);
          flex-shrink: 0;
          padding-top: 3px;
          width: 32px;
        }
        .pod-ep-body { flex: 1; min-width: 0; }
        .pod-ep-title {
          font-family: var(--font-display);
          font-size: 17px;
          font-weight: 700;
          color: #0a0a0a;
          margin: 0 0 8px;
          letter-spacing: -0.015em;
          line-height: 1.3;
        }
        .pod-ep-desc {
          font-family: var(--font-body);
          font-size: 14px;
          color: #555;
          line-height: 1.65;
          margin: 0 0 12px;
        }
        .pod-ep-meta {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #aaa;
          font-weight: 500;
          letter-spacing: 0.02em;
          flex-wrap: wrap;
        }
        .pod-ep-dot { color: #ccc; }
        .pod-ep-apple-link {
          color: var(--purple);
          text-decoration: none;
          font-weight: 600;
          font-size: 12px;
          transition: opacity 0.2s;
        }
        .pod-ep-apple-link:hover { opacity: 0.75; }
        .pod-ep-play {
          flex-shrink: 0;
          width: 42px;
          height: 42px;
          border-radius: 50%;
          background: var(--purple);
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
          margin-top: 2px;
        }
        .pod-ep-play:hover {
          background: var(--purple-hover);
          transform: scale(1.06);
          box-shadow: 0 6px 20px rgba(123,25,188,0.35);
        }
        .pod-ep-cta {
          margin-top: 56px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
          text-align: center;
        }
        .pod-ep-cta-text {
          font-size: 14px;
          color: #999;
          margin: 0;
          letter-spacing: 0.01em;
        }

        /* ── ABOUT ── */
        .pod-about {
          background: #000;
          padding: 110px 24px 120px;
          text-align: center;
          border-top: 1px solid rgba(123,25,188,0.2);
        }
        .pod-about-inner {
          max-width: 640px;
          margin: 0 auto;
        }
        .pod-about-badge {
          display: inline-block;
          padding: 6px 16px;
          background: rgba(123,25,188,0.15);
          border: 1px solid rgba(123,25,188,0.3);
          border-radius: 980px;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--purple-light);
          margin-bottom: 28px;
        }
        .pod-about-h2 {
          font-family: var(--font-display);
          font-size: 40px;
          font-weight: 700;
          letter-spacing: -0.03em;
          line-height: 1.15;
          color: rgba(255,255,255,0.95);
          font-style: italic;
          margin: 0 0 28px;
        }
        .pod-about-body {
          font-family: var(--font-body);
          font-size: 17px;
          color: rgba(255,255,255,0.6);
          line-height: 1.7;
          margin: 0 0 36px;
        }
        .pod-about-rule {
          width: 48px;
          height: 2px;
          background: var(--purple);
          border-radius: 2px;
          margin: 0 auto 24px;
        }
        .pod-about-note {
          font-size: 12px;
          font-weight: 500;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.25);
          margin: 0;
        }

        /* ── RESPONSIVE ── */
        @media (max-width: 1024px) {
          .pod-hero-inner { gap: 48px; }
          .pod-cover-wrap { width: 280px; height: 280px; }
          .pod-hero-h1 { font-size: 46px; }
        }
        @media (max-width: 768px) {
          .pod-hero { padding: 100px 20px 80px; }
          .pod-hero-inner { flex-direction: column; gap: 48px; }
          .pod-hero-right { order: -1; }
          .pod-cover-wrap { width: 240px; height: 240px; margin: 0 auto; }
          .pod-hero-h1 { font-size: 36px; }
          .pod-hero-sub { font-size: 15px; }
          .pod-btn-primary { width: 100%; justify-content: center; }
          .pod-hero-btns { flex-direction: column; align-items: stretch; gap: 14px; }
          .pod-btn-ghost { text-align: center; }
          .pod-stats-strip { flex-wrap: wrap; }
          .pod-stat { flex: 1 1 50%; border-right: none; border-bottom: 1px solid rgba(255,255,255,0.06); }
          .pod-stat:nth-child(odd) { border-right: 1px solid rgba(255,255,255,0.06); }
          .pod-stat:nth-last-child(-n+2) { border-bottom: none; }
          .pod-ep-h2 { font-size: 30px; }
          .pod-about-h2 { font-size: 30px; }
        }
        @media (max-width: 480px) {
          .pod-hero-h1 { font-size: 28px; }
          .pod-ep-card { gap: 16px; }
          .pod-ep-num { display: none; }
          .pod-ep-title { font-size: 15px; }
        }
      `}</style>
    </React.Fragment>
  );
}